import { useState } from 'react';
import {
  SpaceBetween,
  FormField,
  Autosuggest,
  TokenGroup,
  Select,
  Multiselect,
  Input,
  Container,
  Header,
  SelectProps,
  MultiselectProps,
} from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { ExternalPartner } from '@amzn/red-velvet-api';
import { OptionalLabel, EventStepProps } from './Common';
import { PARTNER_OPTIONS } from '../constants';
import { usePapiSuggestions } from '../../../../hooks/usePapiSuggestions';
import { publishKatalMetric } from '../../../katalAnalytics';

const OTHER_PARTNER_NAME_MAX_CHAR = 30;
const MAX_COORDINATORS = 5;

export function CoordinatorsPartnersComponent({
  currentFields,
  updateFieldHandler,
  errors,
  isReadOnly,
  metricPrefix,
}: EventStepProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);

  const partnerOptions: SelectProps.Options = PARTNER_OPTIONS.map((opt) => ({
    label: t(opt.labelKey),
    value: opt.value,
  }));
  const ergSponsors = t('eventDetails.erg_sponsorsOptions', { returnObjects: true }) as Record<
    string,
    string
  >;
  const elgOptions: SelectProps.Options = Object.entries(ergSponsors).map(([value, label]) => ({
    label,
    value,
  }));

  const [coordinatorSearch, setCoordinatorSearch] = useState('');
  const [otherPartnerDraft, setOtherPartnerDraft] = useState<string | null>(null);

  const papiSearch = usePapiSuggestions();
  const existing = new Set(currentFields.additionalCoordinators ?? []);
  const coordinatorSuggestions = papiSearch.suggestions.filter((s) => !existing.has(s.value));

  const otherPartnerValue = otherPartnerDraft ?? currentFields.otherPartner ?? '';

  const coordinatorTokens = (currentFields.additionalCoordinators ?? []).map((alias) => ({
    label: `@${alias}`,
  }));

  const selectedPartner: OptionDefinition | null = currentFields.externalPlanningPartner
    ? ((partnerOptions.find(
        (opt) => (opt as OptionDefinition).value === currentFields.externalPlanningPartner
      ) as OptionDefinition) ?? null)
    : null;

  const selectedElgs: MultiselectProps.Options = (currentFields.elgAffiliations ?? [])
    .map(
      (val) => elgOptions.find((opt) => (opt as OptionDefinition).value === val) as OptionDefinition
    )
    .filter(Boolean);

  const handleAddCoordinator = (alias: string) => {
    const trimmed = alias.trim();
    if (!trimmed) return;
    const existing = currentFields.additionalCoordinators ?? [];
    if (existing.includes(trimmed)) return;
    if (existing.length >= MAX_COORDINATORS) return;
    publishKatalMetric(`${metricPrefix}_coordinatorAdded`, '');
    updateFieldHandler({
      ...currentFields,
      additionalCoordinators: [...existing, trimmed],
    });
    setCoordinatorSearch('');
  };

  const handleDismissCoordinator = (index: number) => {
    const updated = [...(currentFields.additionalCoordinators ?? [])];
    publishKatalMetric(`${metricPrefix}_coordinatorRemoved`, '');
    updated.splice(index, 1);
    updateFieldHandler({ ...currentFields, additionalCoordinators: updated });
  };

  const handlePartnerChange = (option: OptionDefinition) => {
    const partnerValue = option.value as ExternalPartner;
    publishKatalMetric(`${metricPrefix}_partnerSelected`, partnerValue);
    if (partnerValue !== ExternalPartner.OTHER) setOtherPartnerDraft(null);
    updateFieldHandler({
      ...currentFields,
      externalPlanningPartner: partnerValue,
      ...(option.value !== ExternalPartner.OTHER && { otherPartner: undefined }),
    });
  };

  return (
    <Container
      header={<Header variant="h2">{t('teamPartnershipStep.coordinatorsPartners')}</Header>}
    >
      <SpaceBetween size="l">
        <FormField
          description={t('teamPartnershipStep.coordinatorsDescription')}
          label={<OptionalLabel>{t('teamPartnershipStep.coordinatorsLabel')}</OptionalLabel>}
          errorText={errors?.coordinatorSearch}
        >
          <SpaceBetween size="xs">
            <Autosuggest
              filteringType="manual"
              options={coordinatorSuggestions}
              statusType={papiSearch.loading ? 'loading' : undefined}
              placeholder={t('teamPartnershipStep.coordinatorsPlaceholder')}
              value={coordinatorSearch}
              onChange={({ detail }) => {
                setCoordinatorSearch(detail.value);
                papiSearch.search(detail.value);
              }}
              onSelect={({ detail }) => handleAddCoordinator(detail.value)}
              readOnly={isReadOnly}
            />
            <TokenGroup
              alignment="horizontal"
              disableOuterPadding
              items={coordinatorTokens}
              onDismiss={({ detail }) => handleDismissCoordinator(detail.itemIndex)}
              readOnly={isReadOnly}
            />
          </SpaceBetween>
        </FormField>

        <FormField
          description={t('teamPartnershipStep.partnerDescription')}
          label={<OptionalLabel>{t('teamPartnershipStep.partnerLabel')}</OptionalLabel>}
          errorText={errors?.externalPartner}
        >
          <SpaceBetween size="xs">
            <Select
              options={partnerOptions}
              placeholder={t('teamPartnershipStep.partnerPlaceholder')}
              selectedOption={selectedPartner}
              onChange={({ detail }) => handlePartnerChange(detail.selectedOption)}
              readOnly={isReadOnly}
            />
          </SpaceBetween>
        </FormField>

        {currentFields.externalPlanningPartner === ExternalPartner.OTHER && (
          <FormField
            constraintText={t('error.inputTooLong', { maxLength: OTHER_PARTNER_NAME_MAX_CHAR })}
            description={t('teamPartnershipStep.otherPartnerDescription')}
            label={t('teamPartnershipStep.otherPartnerLabel')}
            errorText={errors?.otherPartnerName}
          >
            <Input
              placeholder={t('teamPartnershipStep.otherPartnerPlaceholder')}
              value={otherPartnerValue}
              onChange={({ detail }) =>
                setOtherPartnerDraft(detail.value.slice(0, OTHER_PARTNER_NAME_MAX_CHAR))
              }
              onBlur={() => {
                if (otherPartnerDraft !== null) {
                  updateFieldHandler({ ...currentFields, otherPartner: otherPartnerDraft.trim() });
                  setOtherPartnerDraft(null);
                }
              }}
              readOnly={isReadOnly}
            />
          </FormField>
        )}

        <FormField
          description={t('teamPartnershipStep.elgDescription')}
          label={<OptionalLabel>{t('teamPartnershipStep.elgLabel')}</OptionalLabel>}
          errorText={errors?.elg}
        >
          <Multiselect
            options={elgOptions}
            placeholder={t('teamPartnershipStep.elgPlaceholder')}
            selectedOptions={selectedElgs}
            onChange={({ detail }) =>
              updateFieldHandler({
                ...currentFields,
                elgAffiliations: detail.selectedOptions
                  .map((opt) => opt.value)
                  .filter((v): v is string => !!v),
              })
            }
            readOnly={isReadOnly}
          />
        </FormField>
      </SpaceBetween>
    </Container>
  );
}
